"use client";

import { cn } from "@gettactic/helpers";
import React from "react";
import { useDropzone } from "react-dropzone";
import { FileDropOverlay } from "../FileDropOverlay/FileDropOverlay";

export interface FileDropZoneProps {
  /** Receives the dropped files. */
  onDrop: (files: File[]) => void;
  /** Ignore drags entirely, e.g. while the viewer may not comment. */
  disabled?: boolean;
  /** Headline shown on the veil while a drag is over the area. */
  label?: string;
  /** Second line of the veil, e.g. the size and count limits. */
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Turns an arbitrary area into a drop target: the children render as usual and
 * a veil appears over them while files are dragged in. Clicking and keyboard
 * activation are deliberately off — the area is a whole panel, so opening a
 * file dialog on any stray click would be hostile. Pair it with an explicit
 * attach control.
 */
export const FileDropZone: React.FC<FileDropZoneProps> = ({
  onDrop,
  disabled = false,
  label,
  hint,
  className,
  children
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    multiple: true,
    noClick: true,
    noKeyboard: true
  });

  return (
    <div {...getRootProps({ className: cn("relative", className) })}>
      <input {...getInputProps()} />
      {children}
      <FileDropOverlay
        isActive={isDragActive && !disabled}
        label={label}
        hint={hint}
      />
    </div>
  );
};
